import { useState } from "react";
import { Button, Color, Flex, Text } from "src/elements";
import { Translate, useTranslation } from "src/i18n";
import { AgentDiffOp, AgentValueChange } from "../store/types";
import styled from "styled-components";
import { ReviewButton } from "../../../pages/SpacePlanning/review/reviewDesign";
import { ReviewColor } from "../../../pages/SpacePlanning/review/reviewDesignTokens";

const DiffList = styled(Flex)<{ $embedded: boolean }>`
  ${({ $embedded }) =>
    $embedded &&
    `
    padding: 10px;
    background: ${ReviewColor.subtle};
    border-radius: 8px;
    font-variant-numeric: tabular-nums;
    [data-diff-item] { min-width: 0; overflow-wrap: anywhere; }
    [data-diff-item] > span:first-child { color: ${ReviewColor.muted}; font-size: 12px; }
    [data-diff-item] > span:last-child { color: ${ReviewColor.ink}; font-size: 13px; line-height: 20px; }
  `}
`;

const MAX_COLLAPSED_ROWS = 5;

type T = Translate<"pogAgent">;

const plain = (value: unknown) => (value === null || value === undefined ? "—" : String(value));

/** Zero-based on the wire, first/second/… on screen. */
const position = (value: number | null) => (value === null ? "—" : String(value + 1));

const onOff = (t: T, value: boolean | null) =>
  value === null ? "—" : value ? t("diff.on") : t("diff.off");

const fromTo = <V,>(change: AgentValueChange<V>, show: (value: V | null) => string) => ({
  from: show(change.from),
  to: show(change.to),
});

/**
 * One line per placement, built from the fields the agent reports as changed.
 * A shelf move says so and leaves the position out — a new shelf almost always
 * means a new position too, and only the move matters to the merchandiser.
 */
export const describeDiffOp = (op: AgentDiffOp, t: T): string => {
  const parts: string[] = [];

  if (op.shelf) {
    if (op.shelf.from === null) parts.push(t("diff.addedToShelf", { shelf: plain(op.shelf.to) }));
    else if (op.shelf.to === null) {
      return t("diff.removedFromShelf", { shelf: op.shelf.from });
    } else parts.push(t("diff.shelf", fromTo(op.shelf, plain)));
  } else if (op.sequenceNo) {
    parts.push(t("diff.position", fromTo(op.sequenceNo, position)));
  }

  if (op.facings) parts.push(t("diff.facings", fromTo(op.facings, plain)));
  if (op.facingsRows) parts.push(t("diff.rows", fromTo(op.facingsRows, plain)));
  if (op.orientation) {
    parts.push(
      t(
        "diff.orientation",
        fromTo(op.orientation, (v) => (v === null ? t("diff.default") : v)),
      ),
    );
  }
  const squeeze = (v: boolean | null) => onOff(t, v);
  if (op.squeezeWidth) parts.push(t("diff.squeezeWidth", fromTo(op.squeezeWidth, squeeze)));
  if (op.squeezeHeight) parts.push(t("diff.squeezeHeight", fromTo(op.squeezeHeight, squeeze)));
  if (op.squeezeDepth) parts.push(t("diff.squeezeDepth", fromTo(op.squeezeDepth, squeeze)));
  if (op.capping) {
    for (const [field, label] of [
      ["xCapNum", t("reviewCopy.sideCaps")],
      ["yCapNum", t("reviewCopy.topCaps")],
      ["zCapNum", t("reviewCopy.rearCaps")],
    ]) {
      const before = op.capping.from?.[field] ?? 0;
      const after = op.capping.to?.[field] ?? 0;
      if (before !== after) parts.push(`${label} ${before} → ${after}`);
    }
    if (!parts.length) parts.push(t("reviewCopy.cappingChanged"));
  }

  return parts.length ? parts.join(", ") : t("diff.updated");
};

interface Props {
  embedded?: boolean;
  diff: AgentDiffOp[];
}

export const AgentDiffSummary = ({ embedded = false, diff }: Props) => {
  const { t } = useTranslation("pogAgent");
  const [isExpanded, setIsExpanded] = useState(false);
  const ExpandButton = embedded ? ReviewButton : Button;

  if (!diff.length) {
    return (
      <Text variant="small2" color={Color.textSecondary}>
        {t("diff.none")}
      </Text>
    );
  }

  const visible = isExpanded ? diff : diff.slice(0, MAX_COLLAPSED_ROWS);

  return (
    <DiffList $embedded={embedded} column gap={embedded ? "8px" : "4px"}>
      <Text variant="small1">
        {t(diff.length === 1 ? "diff.change" : "diff.changes", { count: diff.length })}
      </Text>

      {visible.map((op) => (
        <Flex key={op.uniqueId} gap={embedded ? "8px" : "6px"} align="baseline" data-diff-item>
          <Text variant="small2" color={Color.textMain}>
            {op.itemId || op.productCode || "Product"}
          </Text>

          <Text variant="small2" color={Color.textSecondary}>
            {describeDiffOp(op, t)}
          </Text>
        </Flex>
      ))}

      {diff.length > MAX_COLLAPSED_ROWS && (
        <Flex>
          <ExpandButton
            size="small"
            variant="borderless"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? t("diff.showLess") : t("diff.showAll", { count: diff.length })}
          </ExpandButton>
        </Flex>
      )}
    </DiffList>
  );
};
